import Tick from '@assets/icons/tick.svg'
import Warning from '@assets/icons/warning.svg'
import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import Select from 'react-select'

type Options = {
  label: string
  value: string
}

type Props = {
  name: string
  label: string
  options: Array<Options>
  helper?: string
  optional?: boolean
  isChild?: boolean
  infoText?: string
  isMulti?: boolean
  minValues?: number
  maxValues?: number
  extraOnChange?: (e: any) => void
}

const ReactSelect = ({
  name,
  label,
  options,
  helper,
  isChild,
  optional,
  isMulti = false,
  extraOnChange,
  ...rest
}: Props) => {
  const { getFieldState, formState, control } = useFormContext()
  const { error, isTouched, isDirty } = getFieldState(name, formState)
  const [placeholder, setPlaceholder] = useState('Please select')

  const styles = {
    control: (baseStyles: any) => ({
      ...baseStyles,
      borderColor: '#6b728',
      borderRadius: '0',
      minHeight: '48px',
    }),
    input: (baseStyles: any) => ({
      ...baseStyles,
      'input:focus': {
        boxShadow: 'none',
      },
    }),
  }

  // className={`block w-full p-[0.7rem] pr-16 ${
  //   error && 'border-red'
  // } ${!error && isTouched && isDirty && 'border-green'}`}

  return (
    <div
      className={
        isChild
          ? `-mt-6 items-start`
          : `grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 md:gap-4 items-start justify-top mb-4 md:mb-2`
      }
      data-gt-name="componentevent"
    >
      <div className="md:col-span-2 lg:col-span-1">
        <label className="block" htmlFor={name}>
          {label && (
            <span className="inline-block text-base mb-1">
              {label}{' '}
              {optional && (
                <span className="text-xs lowercase whitespace-nowrap">
                  (optional)
                </span>
              )}
            </span>
          )}
          <div className="relative">
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <Select
                  // unstyled
                  blurInputOnSelect={true}
                  isMulti={isMulti}
                  isClearable={false}
                  id={name}
                  placeholder={placeholder}
                  styles={styles}
                  options={options}
                  aria-invalid={error ? 'true' : 'false'}
                  data-gt-action="select-input-interaction"
                  data-gt-label={name}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  onChange={(e) => {
                    if (Array.isArray(e)) {
                      const optionsArray = e
                        .map((option) => option.value)
                        .join(', ')
                      field.onChange(optionsArray)
                    } else if (e && e.value && e.label) {
                      field.onChange(e.value)
                      setPlaceholder(e.value)
                    }
                    if (extraOnChange) extraOnChange(e)
                  }}
                  onBlur={(e) => {
                    field.onBlur(e)
                  }}
                  value={
                    field.value
                      ? options.find((option) => option.value === field.value)
                      : null
                  }
                  {...rest}
                />
              )}
            />
            {error && (
              <Warning className="absolute inset-y-0 right-10 top-4 h-4 pointer-events-none text-red" />
            )}
            {!error && isTouched && isDirty && (
              <Tick className="absolute inset-y-0 right-10 top-4 h-4 pointer-events-none text-green" />
            )}
          </div>
        </label>
        {error && (
          <p className="text-red text-tiny mt-1" role="alert">
            {error.message}
          </p>
        )}
      </div>
      {!isChild && (
        <div className="mt-1 md:mt-7 flex items-center md:min-h-[48px]">
          {helper && <p className="text-tiny leading-5">{helper}</p>}
        </div>
      )}
    </div>
  )
}

export default ReactSelect
